import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useGetEconomySettingsOverviewQuery } from '@client/api';
import { Input, Skeleton } from '@client/toolkit';

import { EconomyCardLoadError } from './economy-card-load-error.component';
import { EconomyCard } from './economy-card.component';
import { SettingsRow } from './settings-row.component';
import {
  ECONOMY_CHIP_OFF_CLASS,
  ECONOMY_CHIP_OK_CLASS,
  ECONOMY_FIELD_SIZE,
  ECONOMY_VALUE_CLASS,
} from '../economy-theme';
import { ERP_SYSTEM_BY_API_VALUE } from '../erp-system.mapping';
import { formatOrganizationNumber } from '../organization-number.format';

/** The rows the card shows, in order. Only the loading state needs them as a list. */
const ROW_LABEL_KEYS = ['name', 'organizationNumber', 'erpSystem', 'chartOfAccounts'] as const;

/**
 * Read-only group identity plus the ERP the group books in. Nothing here is editable —
 * the ERP is chosen during onboarding — so the card has no form and no Rediger button.
 *
 * The card fetches its own data: it is the only part of the Innstillinger tab backed by the
 * API, and the surrounding page still seeds the other cards from the mock.
 */
export function GroupCard() {
  const { t } = useTranslation('economy');

  const { data, isLoading, isError } = useGetEconomySettingsOverviewQuery();

  if (isLoading) {
    return (
      <EconomyCard rowStack title={t('settings.group.title')}>
        {ROW_LABEL_KEYS.map((key) => (
          <SettingsRow key={key} label={t(`settings.group.${key}`)}>
            <div data-testid="group-card-skeleton">
              <Skeleton variant="text" height="h-4" width="w-48" />
            </div>
          </SettingsRow>
        ))}
      </EconomyCard>
    );
  }

  if (isError || data == null) {
    // No `rowStack` here: this is one message, not a label/value pair. Inside a rowStack card
    // the content area is a three-column grid, so a bare child would land in the label column
    // and read as a truncated field rather than as the card's whole state.
    return (
      <EconomyCardLoadError
        title={t('settings.group.title')}
        message={t('settings.group.loadError')}
      />
    );
  }

  const { erpSystem, erpConfigured, chartOfAccounts } = data;

  return (
    <EconomyCard rowStack title={t('settings.group.title')}>
      <SettingsRow label={t('settings.group.name')}>
        <span className={ECONOMY_VALUE_CLASS}>{data.name}</span>
      </SettingsRow>

      <SettingsRow label={t('settings.group.organizationNumber')}>
        <span className={ECONOMY_VALUE_CLASS}>{formatOrganizationNumber(data.organizationNumber)}</span>
      </SettingsRow>

      <SettingsRow label={t('settings.group.erpSystem')}>
        <div className="flex items-center gap-3">
          {erpSystem == null ? (
            // No ERP reported for the corporation. A field — even a disabled one — would read as
            // a value we hold and are merely not letting you edit, and we hold none.
            <span className={ECONOMY_VALUE_CLASS}>—</span>
          ) : (
            /*
             * A disabled `Input` rather than a `Select`: the ERP is fixed at onboarding, so a
             * chevron promising a dropdown is misleading — and `Select`'s disabled state fills
             * with `bg-disabled`, a heavy grey. Same overrides as the read-only email field on
             * Min side. `Input` is `w-full`, so the width comes from the wrapper.
             *
             * The API sends the backend enum member (`Tripletex`) while the i18n keys are
             * camelCase, so the value goes through the mapping rather than into `t()` directly.
             */
            <div className="w-[168px]">
              <Input
                value={t(`erpSystem.${ERP_SYSTEM_BY_API_VALUE[erpSystem]}`)}
                disabled
                size={ECONOMY_FIELD_SIZE}
                aria-label={t('settings.group.erpSystem')}
                className="disabled:border-border disabled:text-gray-500"
              />
            </div>
          )}
          {/* `null` means the Admin API did not answer, so we know nothing either way — showing
              "Ikke konfigurert" would report a misconfiguration we have not established. */}
          {erpConfigured != null && (
            <span
              className={classNames(
                'inline-block rounded-full px-3 py-1',
                erpConfigured ? ECONOMY_CHIP_OK_CLASS : ECONOMY_CHIP_OFF_CLASS,
              )}
            >
              {erpConfigured ? t('settings.group.configured') : t('settings.group.notConfigured')}
            </span>
          )}
        </div>
      </SettingsRow>

      <SettingsRow label={t('settings.group.chartOfAccounts')}>
        <span className={ECONOMY_VALUE_CLASS}>
          {chartOfAccounts.standard == null
            ? t('settings.group.chartOfAccountsSummaryWithoutStandard', {
                lines: chartOfAccounts.lineCount,
                subCategories: chartOfAccounts.subCategoryCount,
              })
            : t('settings.group.chartOfAccountsSummary', {
                lines: chartOfAccounts.lineCount,
                standard: t(`countryTemplate.${chartOfAccounts.standard}`),
                subCategories: chartOfAccounts.subCategoryCount,
              })}
        </span>
      </SettingsRow>
    </EconomyCard>
  );
}

export default GroupCard;
